import Sugar from 'sugar';
import { html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';

import { nsLayoutStyles } from '../../../../ns-styles/index.js';

import { callLambda } from '../../../../helpers/lambda.js';

import { FinalFormController, FormConfig } from '../../../../ns-form/final-form-controller.js';

import { renderFormErrorSpan } from '../../../../helpers/dom.js';

import NSJobComponent from '../../job.js';

import JobSchema from '../../../../models/job-schema.js';
import Company from '../../../../models/company.js';
import Invitation from '../../../../models/invitation.js';
import { createLogger } from '../../../../ns-services/logger.js';

import '../../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/company/onboard-company-delegate.json';
import { jobSchema } from '../../job-schema.js';

const logger = createLogger('ns-onboard-company-delegate');

const DELEGATE_IMAGE = '/images/jobs/companies-house-verification.svg';
const DELEGATE_IMAGE_LABEL = 'Delegate invitation illustration';

interface FormValues {
  companyName: string;
  contactName: string;
  contactEmail: string;
  jobTitle: string;
}

@customElement(NSOnboardCompanyDelegate.is)
export default class NSOnboardCompanyDelegate extends NSJobComponent {
  static get is() {
    return 'ns-onboard-company-delegate';
  }

  private __createFormController: FinalFormController<FormValues>;

  private __errors: Partial<Record<keyof FormValues, string>> = {};

  @state()
  private __company?: Company;

  @state()
  private __acceptDelegation: boolean = false;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { skip: { type: 'hidden' }, invite: { type: 'hidden' } } });
  }

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      .field + .field {
        margin-top: var(--wa-space-m);
      }
    `,
  ];

  constructor() {
    super();

    this.__createFormController = new FinalFormController(this, {
      onSubmit: async (values) => await this.__createDelegateInvitationFormSubmit(values),
      validate: (values) => {
        const errors: Partial<Record<keyof FormValues, string>> = {};

        if (!values.companyName) errors.companyName = 'Missing required company name';
        if (!values.contactEmail) errors.contactEmail = 'Missing required contact email';
        if (!values.contactName) errors.contactName = 'Missing required contact name';
        if (!values.jobTitle) errors.jobTitle = 'Missing required job title';

        return errors;
      },
    } as FormConfig<FormValues>);
  }

  async connectedCallback() {
    try {
      await super.connectedCallback();

      if (!this._db) throw new Error('Unable to find db service');

      if (!this._directory) throw new Error('Unable to find NSDirectoryService');

      if (!this._auth) throw new Error('Unable to find auth service');

      this.__company = this._directory.company;
      if (!this.__company) throw new Error('Could not find a company linked to the directory service');

      this.__createFormController.form.initialize({
        companyName: this.__company.name,
        contactName: '',
        contactEmail: '',
      });

      this.__acceptDelegation = this.getMetadataValue('INVITE_DELEGATE') === 'TRUE';
    } catch (err: unknown) {
      this.failJob(err, 'Unexpected error occurred while loading companies');
    }
  }

  private async __createDelegateInvitationFormSubmit(values: Partial<FormValues>) {
    this.__errors = {};

    if (!values.companyName) this.__errors.companyName = 'Missing required company name';
    if (!values.contactEmail) this.__errors.contactEmail = 'Missing required contact email';
    if (!values.contactName) this.__errors.contactName = 'Missing required contact name';
    if (!values.jobTitle) this.__errors.jobTitle = 'Missing required job title';

    if (Object.keys(this.__errors).length > 0) {
      return logger.error('There are some missing required values');
    }

    if (!this._db) throw new Error('Missing required db service');
    if (!this._auth) throw new Error('Missing required auth service');
    if (!this._directory) throw new Error('Missing required directory service');
    if (!this._directory.me) throw new Error('Missing directory service user profile');
    if (!this.__company) throw new Error('Missing required profile company');
    if (!this._job) throw new Error('Missing required job');

    this.loading('Inviting Delegate');
    const profile = this._directory.me;
    const invitation = this._db.createObject<Invitation>('invitation');
    invitation.inviter.personId = profile.id;
    invitation.inviter.email = profile.email;
    invitation.inviter.companyId = this.__company.id;

    invitation.invitee.name = values.contactName ? values.contactName : null;
    invitation.invitee.email = values.contactEmail ? values.contactEmail : null;
    invitation.invitee.jobTitle = values.jobTitle ? values.jobTitle : null;
    invitation.invitee.company.id = this.__company.id;
    invitation.invitee.company.name = this.__company.name;
    invitation.expiryDate = Sugar.Date.create('in 1 day');

    try {
      const output = await callLambda(
        'onboarding/create-invitation',
        'POST',
        this._auth.token,
        JSON.stringify({
          userId: profile.userId,
          personId: profile.id,
          oauthApp: sessionStorage.getItem('oauthApp'),
          companyId: this.__company.id,
          // This open card lets its assignee invite their delegate before they hold PEOPLE_INVITE
          jobId: this._job.id,
          invitations: [invitation],
        }),
      );
      const [invitationDb] = (output.res as unknown as { invitations?: Invitation[] }).invitations ?? [];
      if (!invitationDb) throw new Error('Unable to invite the delegate.');

      await this.updateMetadata('INVITATION_ID', invitationDb.id);
      await this.outcome('invite');
    } catch (err) {
      this.showError(err, 'Unable to invite the delegate.');
    } finally {
      this.loaded();
    }
  }

  private async __updateJobMetadata() {
    await this.updateMetadata('INVITE_DELEGATE', 'TRUE');
    this.__acceptDelegation = true;
  }

  private __renderFormState() {
    const { form, register } = this.__createFormController;
    const formState = form.getState();

    const visibleError = (field: keyof FormValues) => {
      const shouldShow = formState.submitFailed && !formState.dirtyFieldsSinceLastSubmit?.[field];
      const message = formState.errors?.[field];
      return shouldShow && message ? { [field]: message } : {};
    };

    return html`
      <ns-job-layout image=${DELEGATE_IMAGE} image-label=${DELEGATE_IMAGE_LABEL}>
        <p slot="intro">Please fill in the below form to invite the delegate.</p>
        <div class="job-panel">
          <form
            id="invite-delegate-form"
            @submit=${(e: CustomEvent) => {
              e.preventDefault();
              form.submit();
            }}
          >
            <div class="field layout vertical">
              <ns-input-field
                stacked
                required
                ${register('companyName')}
                placeholder="Company Name"
                label="Company Name*:"
                .value=${this.__company?.name}
                autocomplete="organization"
              ></ns-input-field>
              ${renderFormErrorSpan('companyName', visibleError('companyName'))}
            </div>
            <div class="field layout vertical">
              <ns-input-field
                stacked
                required
                ${register('contactName')}
                placeholder="Contact Name"
                label="Contact Name*:"
                autocomplete="name"
              ></ns-input-field>
              ${renderFormErrorSpan('contactName', visibleError('contactName'))}
            </div>
            <div class="field layout vertical">
              <ns-input-field
                stacked
                required
                ${register('contactEmail')}
                placeholder="Contact Email"
                label="Contact Email*:"
                autocomplete="email"
                type="email"
              ></ns-input-field>
              ${renderFormErrorSpan('contactEmail', visibleError('contactEmail'))}
            </div>
            <div class="field layout vertical">
              <ns-input-field
                stacked
                required
                ${register('jobTitle')}
                placeholder="Contact Job Title"
                label="Contact Job Title*:"
                autocomplete="organization-title"
              ></ns-input-field>
              ${renderFormErrorSpan('jobTitle', visibleError('jobTitle'))}
            </div>
          </form>
        </div>
      </ns-job-layout>
    `;
  }

  private __renderPromptState() {
    return html`
      <ns-job-layout image=${DELEGATE_IMAGE} image-label=${DELEGATE_IMAGE_LABEL}>
        <div class="job-panel">
          <p>Would you like to invite a delegate to send the invitations to the company's employees?</p>
        </div>
      </ns-job-layout>
    `;
  }

  render() {
    return when(
      this.__acceptDelegation,
      () => this.__renderFormState(),
      () => this.__renderPromptState(),
    );
  }

  renderActions() {
    if (this.__acceptDelegation) {
      const { form } = this.__createFormController;
      return html`
        <wa-button variant="brand" type="button" ?disabled=${form.getState().submitting} @click=${() => form.submit()}>
          Send Invitation
        </wa-button>
      `;
    }
    return html`
      <wa-button variant="brand" appearance="outlined" type="button" @click=${() => this.outcome('skip')}>No</wa-button>
      <wa-button variant="brand" type="button" @click=${() => this.__updateJobMetadata()}>Yes</wa-button>
    `;
  }
}
